import { useEffect, useState } from 'react'
import { Gift, Sparkles, Lock, AlertCircle, PackageX, Coins, CheckCircle2, ImageOff, Settings } from 'lucide-react'
import { Link } from 'react-router-dom'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import PageHeader from '@/components/PageHeader'
import { prizesAPI } from '@/lib/api'
import { useAuth } from '@/hooks/useAuth'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog'

const TIER_ACCENT = {
  25: { toneVar: 'rgb(var(--gold-rgb))',   text: 'text-gold',    bg: 'bg-gold',    label: 'Hoofdprijs' },
  15: { toneVar: 'rgb(var(--peach-rgb))',  text: 'text-peach',   bg: 'bg-peach',   label: null },
  10: { toneVar: 'rgb(var(--signal-rgb))', text: 'text-signal',  bg: 'bg-signal',  label: null },
   5: { toneVar: 'rgb(var(--acid-rgb))',   text: 'text-acid',    bg: 'bg-acid',    label: null },
   1: { toneVar: 'rgb(var(--live-rgb))',   text: 'text-live',    bg: 'bg-live',    label: null },
}

export default function PrizesPage() {
  const { user } = useAuth()
  const isAdmin = user?.role === 'admin' || user?.role === 'superadmin'
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [claiming, setClaiming] = useState(null)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(null)
  const [confirmPrize, setConfirmPrize] = useState(null)

  const load = async () => {
    try { const r = await prizesAPI.listCategories(); setData(r.data); setError(null) }
    catch (e) { setError(e.response?.data?.error ?? e.message) }
    finally { setLoading(false) }
  }
  useEffect(() => { load() }, [])

  const askClaim = (prize) => setConfirmPrize(prize)

  const doClaim = async () => {
    const prize = confirmPrize
    if (!prize) return
    setConfirmPrize(null)
    setClaiming(prize.id); setError(null); setSuccess(null)
    try {
      const r = await prizesAPI.claim(prize.id)
      setSuccess(`Geclaimd! Haal "${prize.name}" af aan de kraam. Nieuwe saldo: ${r.data.new_balance} punten.`)
      load()
    } catch (e) {
      setError(e.response?.data?.error ?? e.message)
    } finally { setClaiming(null) }
  }

  if (loading) return <div className="p-6 text-muted-foreground">Laden…</div>
  if (!data) return <div className="p-6 text-red-500">Kon niet laden</div>

  return (
    <div className="space-y-6">
      <PageHeader
        icon={Gift}
        
        eyebrow="Bear & Bockie LAN"
        title="Prijzenkraam"
        subtitle="Wissel je verdiende punten in voor de prijzen aan de kraam"
        actions={
          <div className="flex gap-2">
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-yellow-500/40 bg-yellow-500/10">
              <Coins className="h-4 w-4 text-yellow-500" />
              <span className="font-bold tabular-nums">{data.balance}</span>
              <span className="text-xs text-muted-foreground">punten</span>
            </div>
            {isAdmin && (
              <Button asChild variant="outline" size="sm">
                <Link to="/prijzenkraam/admin"><Settings className="h-4 w-4 mr-2" />Admin</Link>
              </Button>
            )}
          </div>
        }
      />

      {/* Status banner */}
      {!data.shop_open && (
        <Card className="border-yellow-500/40 bg-yellow-500/5">
          <CardContent className="p-4 flex items-center gap-3">
            <Lock className="h-5 w-5 text-yellow-500 shrink-0" />
            <div>
              <div className="font-semibold text-yellow-500">De kraam is nog gesloten</div>
              <div className="text-sm text-muted-foreground">Kijk gerust rond — claimen kan zodra de admin de kraam opent.</div>
            </div>
          </CardContent>
        </Card>
      )}

      {data.shop_open && (
        <Card className="border-emerald-500/40 bg-gradient-to-br from-emerald-500/10 to-cyan-500/5">
          <CardContent className="p-4 flex items-center gap-3">
            <Sparkles className="h-5 w-5 text-emerald-400 shrink-0 animate-pulse" />
            <div>
              <div className="font-semibold text-emerald-400">De kraam is OPEN!</div>
              <div className="text-sm text-muted-foreground">Kies je prijs zolang de voorraad strekt. Sommige categorieën hebben een limiet per persoon — zie badge boven de categorie.</div>
            </div>
          </CardContent>
        </Card>
      )}

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Fout</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      {success && (
        <Alert className="border-emerald-500/40 bg-emerald-500/5">
          <CheckCircle2 className="h-4 w-4 text-emerald-400" />
          <AlertDescription className="text-emerald-400">{success}</AlertDescription>
        </Alert>
      )}

      {/* Categories */}
      {data.categories.map(cat => (
        <CategorySection
          key={cat.id}
          cat={cat}
          balance={data.balance}
          shopOpen={data.shop_open}
          onClaim={askClaim}
          claiming={claiming}
        />
      ))}

      <MyClaims />

      <Dialog open={!!confirmPrize} onOpenChange={(o) => !o && setConfirmPrize(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Prijs claimen?</DialogTitle>
            <DialogDescription>
              Je claimt <strong>{confirmPrize?.name}</strong> voor{' '}
              <strong>{confirmPrize?.price ?? data.categories.find(c => c.id === confirmPrize?.category_id)?.price} punten</strong>.
              Je haalt hem daarna af aan de kraam — de organisatie krijgt automatisch een melding.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmPrize(null)}>Nee, toch niet</Button>
            <Button onClick={doClaim}>Ja, claim voor mij</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

function CategorySection({ cat, balance, shopOpen, onClaim, claiming }) {
  const accent = TIER_ACCENT[cat.price] || TIER_ACCENT[5]
  const atLimit = cat.max_per_user > 0 && cat.my_claim_count >= cat.max_per_user
  const limitLabel = cat.max_per_user > 0
    ? (cat.max_per_user === 1 ? 'Max 1 per persoon' : `Max ${cat.max_per_user} per persoon`)
    : null
  return (
    <div>
      <div className="flex items-baseline gap-3 mb-3 flex-wrap">
        <div className="h-9 w-9 rounded-[4px] border border-border bg-card flex items-center justify-center shrink-0"
             style={{ borderLeft: `2px solid ${accent.toneVar}` }}>
          <span className={`${accent.text} font-bold text-sm tabular-nums`}>{cat.price}</span>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h2 className="text-lg font-bold">{cat.name}</h2>
            {limitLabel && (
              <Badge variant="outline" className="text-xs">
                {limitLabel}{cat.max_per_user > 0 && ` · jij: ${cat.my_claim_count}/${cat.max_per_user}`}
              </Badge>
            )}
          </div>
          {cat.description && <p className="text-xs text-muted-foreground">{cat.description}</p>}
        </div>
      </div>
      <div className="grid gap-3 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {cat.prizes.map(p => (
          <PrizeCard
            key={p.id}
            prize={{ ...p, price: cat.price }}
            accent={accent}
            balance={balance}
            shopOpen={shopOpen}
            catAtLimit={atLimit}
            catName={cat.name}
            onClaim={onClaim}
            claiming={claiming === p.id}
          />
        ))}
      </div>
    </div>
  )
}

function PrizeCard({ prize, accent, balance, shopOpen, catAtLimit, catName, onClaim, claiming }) {
  const soldOut = prize.remaining <= 0
  const cantAfford = balance < prize.price
  const already = prize.my_claim_count > 0
  const disabled = !shopOpen || soldOut || cantAfford || catAtLimit || claiming

  return (
    <Card className={`overflow-hidden transition-all ${soldOut ? 'opacity-50' : 'hover:shadow-lg hover:border-primary/40'}`}>
      <div className="relative aspect-square bg-muted flex items-center justify-center">
        {prize.image_url ? (
          <img src={prize.image_url} alt={prize.name} className="w-full h-full object-cover" />
        ) : (
          <ImageOff className="h-10 w-10 text-muted-foreground/40" />
        )}
        <div className={`absolute top-2 right-2 h-10 w-10 rounded-full bg-gradient-to-br ${accent.grad} flex items-center justify-center shadow-lg`}>
          <span className="text-white font-bold text-sm tabular-nums">{prize.price}</span>
        </div>
        {soldOut && (
          <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
            <Badge variant="outline" className="text-red-400 border-red-400 text-sm">
              <PackageX className="h-3 w-3 mr-1" /> Uitverkocht
            </Badge>
          </div>
        )}
      </div>
      <CardContent className="p-3 space-y-2">
        <div className="font-semibold text-sm truncate">{prize.name}</div>
        {prize.description && <p className="text-xs text-muted-foreground line-clamp-2">{prize.description}</p>}
        <div className="flex items-center justify-between text-xs">
          <span className={soldOut ? 'text-red-400' : prize.remaining <= 3 ? 'text-yellow-500' : 'text-muted-foreground'}>
            {prize.remaining === 1 ? 'laatste exemplaar' : `${prize.remaining} beschikbaar`}
          </span>
          {already && <Badge variant="outline" className="text-xs text-emerald-400 border-emerald-400/40">✓ Jouw claim</Badge>}
        </div>
        <Button
          size="sm"
          variant={disabled ? 'outline' : 'default'}
          className={`w-full ${!disabled ? accent.bg + ' hover:opacity-90 text-black' : 'opacity-60 grayscale'}`}
          disabled={disabled}
          onClick={() => onClaim(prize)}
        >
          {claiming ? 'Claimen…' :
           !shopOpen ? 'Kraam gesloten' :
           soldOut ? 'Uitverkocht' :
           catAtLimit ? `Max ${catName} bereikt` :
           cantAfford ? `${prize.price - balance} pt tekort` :
           `Claim voor ${prize.price} pt`}
        </Button>
      </CardContent>
    </Card>
  )
}

function MyClaims() {
  const [claims, setClaims] = useState([])
  useEffect(() => {
    prizesAPI.listMyClaims().then(r => setClaims(r.data.claims || [])).catch(() => {})
  }, [])
  if (claims.length === 0) return null
  return (
    <Card>
      <CardHeader className="pb-2"><CardTitle className="text-base">Mijn geclaimde prijzen</CardTitle></CardHeader>
      <CardContent className="space-y-1.5">
        {claims.map(c => (
          <div key={c.id} className="flex items-center gap-3 text-sm p-2 rounded border border-border">
            {c.image_url ? <img src={c.image_url} alt="" className="h-8 w-8 object-cover rounded" /> : <Gift className="h-8 w-8 text-muted-foreground/40" />}
            <div className="flex-1 min-w-0">
              <div className="font-semibold truncate">{c.prize_name}</div>
              <div className="text-xs text-muted-foreground">{c.category_name} · {c.points_spent} punten · {new Date(c.claimed_at).toLocaleString('nl-BE')}</div>
            </div>
            {c.handed_over_at
              ? <Badge className="bg-emerald-500 text-black text-xs">✓ Opgehaald</Badge>
              : <Badge variant="outline" className="text-yellow-500 border-yellow-500/40 text-xs">Nog op te halen</Badge>}
          </div>
        ))}
      </CardContent>
    </Card>
  )
}
